import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Container, Card, Spinner, Alert, Button, Form, Badge } from 'react-bootstrap';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheckCircle, faPaperPlane, faClipboardList } from '@fortawesome/free-solid-svg-icons';

const AnamneseTabletPage = () => {
    const { codigo } = useParams();
    const [isLoading, setIsLoading] = useState(true);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isFinished, setIsFinished] = useState(false);
    const [error, setError] = useState(null);
    const [pacienteData, setPacienteData] = useState(null);
    const [respostas, setRespostas] = useState({});

    useEffect(() => {
        const validarCodigo = async () => {
            setIsLoading(true);
            try {
                const response = await axios.get(`/api/public/anamnese/${codigo}`);
                setPacienteData(response.data);
            } catch (err) {
                setError("Código inválido ou expirado. Por favor, solicite um novo código na recepção.");
            } finally {
                setIsLoading(false);
            }
        };
        validarCodigo();
    }, [codigo]);

    // ── Booleano / texto_livre: toggle simples ───────────────────────────────
    const handleInputChange = (perguntaId, value) => {
        setRespostas(prev => {
            if (prev[perguntaId] === value) {
                const updated = { ...prev };
                delete updated[perguntaId];
                return updated;
            }
            return { ...prev, [perguntaId]: value };
        });
    };

    // ✅ Múltipla escolha: gerencia array de seleções com toggle
    const handleMultiplaEscolha = (perguntaId, opcao) => {
        setRespostas(prev => {
            const atual = Array.isArray(prev[perguntaId]) ? prev[perguntaId] : [];

            const novas = atual.includes(opcao)
                ? atual.filter(o => o !== opcao)   // desmarca
                : [...atual, opcao];               // marca

            return {
                ...prev,
                [perguntaId]: novas.length > 0 ? novas : undefined
            };
        });
    };

    // ── Renderiza o input de cada pergunta ───────────────────────────────────
    const renderPerguntaInput = (p) => {
        const semResposta = respostas[p.id] === '__sem_resposta__';

        switch (p.tipo_pergunta) {

            // ── Booleano ─────────────────────────────────────────────────────
            case 'booleano':
                return (
                    <div className="d-flex gap-2 flex-wrap align-items-center">
                        <Button
                            variant={respostas[p.id] === 'Sim' ? 'success' : 'outline-success'}
                            size="sm"
                            onClick={() => handleInputChange(p.id, 'Sim')}
                            style={{ minWidth: '70px' }}
                            disabled={semResposta}
                        >
                            Sim
                        </Button>
                        <Button
                            variant={respostas[p.id] === 'Não' ? 'danger' : 'outline-danger'}
                            size="sm"
                            onClick={() => handleInputChange(p.id, 'Não')}
                            style={{ minWidth: '70px' }}
                            disabled={semResposta}
                        >
                            Não
                        </Button>
                        <Button
                            variant={semResposta ? 'secondary' : 'outline-secondary'}
                            size="sm"
                            onClick={() => handleInputChange(p.id, '__sem_resposta__')}
                            style={{ minWidth: '110px' }}
                        >
                            <i className="bi bi-slash-circle me-1"></i>
                            Sem Resposta
                        </Button>
                    </div>
                );

            // ✅ Múltipla escolha: botões pill com toggle ──────────────────────
            case 'multipla_escolha': {
                const opcoes = p.opcoes
                    ? (typeof p.opcoes === 'string' ? JSON.parse(p.opcoes) : p.opcoes)
                    : [];

                const selecionadas = Array.isArray(respostas[p.id]) ? respostas[p.id] : [];

                return (
                    <div className="d-flex flex-column gap-2" style={{ minWidth: '260px', maxWidth: '380px' }}>

                        {/* ✅ Botões pill — um por opção, fáceis de tocar no tablet */}
                        <div className="d-flex flex-wrap gap-2">
                            {opcoes.map((opcao, index) => {
                                const isSelecionada = selecionadas.includes(opcao);
                                return (
                                    <Button
                                        key={index}
                                        size="sm"
                                        variant={isSelecionada ? 'primary' : 'outline-primary'}
                                        disabled={semResposta}
                                        onClick={() => handleMultiplaEscolha(p.id, opcao)}
                                        className="d-flex align-items-center gap-1"
                                        style={{ borderRadius: '20px' }}
                                    >
                                        {isSelecionada && <i className="bi bi-check-lg"></i>}
                                        {opcao}
                                    </Button>
                                );
                            })}
                        </div>

                        {/* ✅ Contador de seleções */}
                        {selecionadas.length > 0 && !semResposta && (
                            <small className="text-muted">
                                <i className="bi bi-check2-all me-1 text-success"></i>
                                {selecionadas.length} opção(ões) selecionada(s)
                            </small>
                        )}

                        <Button
                            variant={semResposta ? 'secondary' : 'outline-secondary'}
                            size="sm"
                            onClick={() => handleInputChange(p.id, '__sem_resposta__')}
                            style={{ borderRadius: '20px', alignSelf: 'flex-start' }}
                        >
                            <i className="bi bi-slash-circle me-1"></i>
                            Sem Resposta
                        </Button>
                    </div>
                );
            }

            // ── Texto livre ───────────────────────────────────────────────────
            case 'texto_livre':
            default:
                return (
                    <div className="d-flex flex-column gap-2">
                        <Form.Control
                            as="textarea"
                            rows={2}
                            placeholder={semResposta ? 'Paciente optou por não responder' : 'Sua resposta...'}
                            value={semResposta ? '' : (respostas[p.id] || '')}
                            onChange={(e) => handleInputChange(p.id, e.target.value)}
                            disabled={semResposta}
                            style={{ minWidth: '260px', maxWidth: '420px', resize: 'none' }}
                        />
                        <Button
                            variant={semResposta ? 'secondary' : 'outline-secondary'}
                            size="sm"
                            onClick={() => handleInputChange(p.id, '__sem_resposta__')}
                            style={{ maxWidth: '420px' }}
                        >
                            <i className="bi bi-slash-circle me-1"></i>
                            Sem Resposta
                        </Button>
                    </div>
                );
        }
    };

    // ── Submit ────────────────────────────────────────────────────────────────
    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);
        try {
            const todasRespostas = pacienteData.perguntas.map(p => {
                const valor = respostas[p.id];

                // "Sem Resposta" → null
                if (valor === '__sem_resposta__') {
                    return { pergunta_id: p.id, resposta: null };
                }

                // ✅ Múltipla escolha (array) → serializa com ", "
                if (Array.isArray(valor)) {
                    return {
                        pergunta_id: p.id,
                        resposta: valor.length > 0 ? valor.join(', ') : null
                    };
                }

                // Demais tipos → string ou null
                return {
                    pergunta_id: p.id,
                    resposta: valor || null
                };
            });

            const payload = {
                tipo_ficha: pacienteData.tipo_ficha,
                respostas: todasRespostas
            };

            await axios.post(`/api/public/anamnese/${codigo}/submit`, payload);
            setIsFinished(true);
        } catch (err) {
            alert("Erro ao enviar anamnese. Verifique sua conexão.");
        } finally {
            setIsSubmitting(false);
        }
    };

    // ── Estados de carregamento / erro / finalizado ───────────────────────────

    if (isLoading) {
        return (
            <Container className="d-flex justify-content-center align-items-center vh-100">
                <div className="text-center">
                    <Spinner animation="border" variant="primary" className="mb-3" />
                    <h4>Carregando sua ficha...</h4>
                </div>
            </Container>
        );
    }

    if (error) {
        return (
            <Container className="mt-5">
                <Alert variant="danger">{error}</Alert>
            </Container>
        );
    }

    if (isFinished) {
        return (
            <Container className="d-flex justify-content-center align-items-center vh-100">
                <Card className="text-center shadow-lg p-5 border-0" style={{ maxWidth: '500px' }}>
                    <FontAwesomeIcon icon={faCheckCircle} size="5x" className="text-success mb-4" />
                    <h3>Obrigado, {pacienteData?.paciente_nome}!</h3>
                    <p className="lead text-muted">Sua ficha de anamnese foi enviada com sucesso.</p>
                </Card>
            </Container>
        );
    }

    // ── Render principal ──────────────────────────────────────────────────────

    return (
        <Container className="py-4">
            <Card className="shadow-sm border-0">

                <Card.Header className="bg-primary text-white py-3">
                    <div className="d-flex align-items-center gap-2 mb-1">
                        <FontAwesomeIcon icon={faClipboardList} size="lg" />
                        <h4 className="mb-0">Ficha de Anamnese</h4>
                    </div>
                    <div className="d-flex flex-wrap gap-2 align-items-center mt-1">
                        <small>Paciente: <strong>{pacienteData?.paciente_nome}</strong></small>
                        <span className="opacity-50">|</span>
                        <small>Especialidade: <strong>{pacienteData?.especialidade}</strong></small>
                        {pacienteData?.nome_ficha && (
                            <>
                                <span className="opacity-50">|</span>
                                <Badge bg="light" text="primary" className="fs-6 px-2 py-1">
                                    <FontAwesomeIcon icon={faClipboardList} className="me-1" />
                                    {pacienteData.nome_ficha}
                                </Badge>
                            </>
                        )}
                    </div>
                </Card.Header>

                <Card.Body className="p-0">
                    <Alert variant="info" className="rounded-0 mb-0 border-0 border-bottom">
                        Suas respostas ajudam nossa equipe médica a oferecer o melhor tratamento possível.
                    </Alert>

                    <Form onSubmit={handleSubmit}>
                        <div className="list-group list-group-flush">
                            {pacienteData?.perguntas?.map((p, index) => (
                                <div
                                    key={p.id}
                                    className="list-group-item px-4 py-3"
                                    style={{ backgroundColor: index % 2 === 0 ? '#ffffff' : '#f8f9fa' }}
                                >
                                    <div className="d-flex align-items-center justify-content-between gap-4 flex-wrap">

                                        <div className="d-flex align-items-center gap-2 flex-grow-1">
                                            <Badge
                                                bg="primary"
                                                className="rounded-circle d-flex align-items-center justify-content-center flex-shrink-0"
                                                style={{ width: '28px', height: '28px', fontSize: '0.75rem' }}
                                            >
                                                {index + 1}
                                            </Badge>
                                            <Form.Label
                                                htmlFor={`pergunta-${p.id}`}
                                                className="mb-0 fw-semibold"
                                                style={{ fontSize: '0.95rem' }}
                                            >
                                                {p.pergunta}
                                            </Form.Label>
                                        </div>

                                        <div className="flex-shrink-0">
                                            {renderPerguntaInput(p)}
                                        </div>

                                    </div>
                                </div>
                            ))}
                        </div>

                        <div className="d-grid p-4">
                            <Button variant="success" size="lg" type="submit" disabled={isSubmitting}>
                                {isSubmitting
                                    ? <Spinner size="sm" />
                                    : <><FontAwesomeIcon icon={faPaperPlane} className="me-2" />Enviar Respostas</>
                                }
                            </Button>
                        </div>
                    </Form>
                </Card.Body>

            </Card>
        </Container>
    );
};

export default AnamneseTabletPage;
